import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';

// Webpack mangles Leaflet's internal asset URL resolution — mergeOptions restores it.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// Catches Leaflet asset-load failures so they degrade to a message rather than
// crashing the entire registration form.
class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ height: 280, borderRadius: 8, backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>Map unavailable. You can continue without pinning a location.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Defined at module level — components defined inside a parent are remounted on every
// parent render, which destroys Leaflet's internal map state on each keystroke.
function MapPickerInner({ pickedLat, pickedLng, flyTo, onPick }) {
  const map = useMapEvents({ click: e => onPick(e.latlng.lat, e.latlng.lng) });
  useEffect(() => { if (flyTo) map.flyTo(flyTo, 14); }, [flyTo, map]);
  return pickedLat != null && pickedLng != null ? (
    <Marker
      position={[pickedLat, pickedLng]}
      draggable
      eventHandlers={{ dragend: e => onPick(e.target.getLatLng().lat, e.target.getLatLng().lng) }}
    />
  ) : null;
}

const PAYMENT_METHODS = [
  { value: 'cash',                 label: 'Cash only' },
  { value: 'upi',                  label: 'UPI' },
  { value: 'razorpay_link',        label: 'Razorpay Payment Link' },
  { value: 'razorpay_integration', label: 'Razorpay Integration' },
];

const PAYOUT_FREQS = [
  { value: 'manual',   label: 'Manual (on request)' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly',  label: 'Monthly' },
];

export default function CentreSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const token          = searchParams.get('token') || '';

  const [prefill,    setPrefill]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg,   setErrorMsg]   = useState('');
  const [flyTo,      setFlyTo]      = useState(null);

  const [form, setForm] = useState({
    adminFirstName: '', adminLastName: '', adminPassword: '', adminPhone: '',
    address: '', pincode: '', invoicePrefix: '',
    paymentMethod: '', paymentLink: '', healerSharePercent: '',
    payoutFrequency: '', payoutDay: '',
    gstRegistered: false, gstin: '', legalEntityName: '',
    mcksAffiliated: false,
    consentGiven:  false,
    pickedLat: null, pickedLng: null,
  });

  useEffect(() => {
    if (!token) {
      setTokenError('No setup token found. Please use the link from your verification email.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/centre/setup-info?token=${token}`);
        setPrefill(res.data.data);
      } catch (err) {
        const code = err.response?.data?.code;
        setTokenError(
          code === 'TOKEN_EXPIRED'
            ? 'This setup link has expired (7-day limit). Please email support@lifechiai.com for a new link.'
            : 'This setup link is invalid or already used. Please email support@lifechiai.com.'
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleMapPick = (lat, lng) => {
    setForm(f => ({ ...f, pickedLat: lat, pickedLng: lng }));
  };

  const handlePincodeBlur = async () => {
    const pincode = form.pincode.trim();
    if (!pincode) return;
    try {
      const res = await api.get(`/centre/geocode?pincode=${encodeURIComponent(pincode)}`);
      if (res.data.data) {
        const { lat, lng } = res.data.data;
        setForm(f => ({ ...f, pickedLat: lat, pickedLng: lng }));
        setFlyTo([lat, lng]);
      }
    } catch {
      // Non-fatal — geocode failure does not block the form
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.adminFirstName.trim())   { setErrorMsg('First name is required.');                  return; }
    if (!form.adminPassword)           { setErrorMsg('Password is required.');                    return; }
    if (form.adminPassword.length < 8) { setErrorMsg('Password must be at least 8 characters.'); return; }
    if (!/^[A-Z]{2,6}$/.test(form.invoicePrefix.trim().toUpperCase())) { setErrorMsg('Invoice prefix is required: 2 to 6 letters (A to Z only).'); return; }
    if (form.paymentMethod === 'razorpay_link' && !form.paymentLink.trim()) {
      setErrorMsg('Payment link URL is required for Razorpay Payment Link.');
      return;
    }
    if (form.gstRegistered && !form.gstin.trim()) {
      setErrorMsg('GSTIN is required when GST Registered is enabled.');
      return;
    }
    if (!form.consentGiven) {
      setErrorMsg('You must accept the privacy policy to continue.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/centre/complete-setup', {
        token,
        adminFirstName:     form.adminFirstName.trim(),
        adminLastName:      form.adminLastName.trim()    || undefined,
        adminPassword:      form.adminPassword,
        adminPhone:         form.adminPhone.trim()       || undefined,
        address:            form.address.trim()          || undefined,
        pincode:            form.pincode.trim()          || undefined,
        invoicePrefix:      form.invoicePrefix.trim()    || undefined,
        centreType:         'pranic_healing',
        paymentMethod:      form.paymentMethod           || undefined,
        paymentLink:        form.paymentLink.trim()      || undefined,
        healerSharePercent: form.healerSharePercent !== '' ? Number(form.healerSharePercent) : undefined,
        payoutFrequency:    form.payoutFrequency         || undefined,
        payoutDay:          form.payoutDay !== ''        ? Number(form.payoutDay) : undefined,
        gstRegistered:      form.gstRegistered,
        gstin:              form.gstRegistered ? form.gstin.trim()          : undefined,
        legalEntityName:    form.gstRegistered ? form.legalEntityName.trim(): undefined,
        mcksAffiliated:     form.mcksAffiliated,
        consentGiven:       form.consentGiven,
        pickedLat:          form.pickedLat  ?? undefined,
        pickedLng:          form.pickedLng  ?? undefined,
      });
      navigate('/register/success');
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.card}><p style={{ color: '#6b7280' }}>Loading…</p></div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <h1 style={S.title}>Setup Link Problem</h1>
          <div style={S.errorBanner}>{tokenError}</div>
          <p style={{ fontSize: 14, color: '#6b7280', marginTop: 16 }}>
            Email{' '}
            <a href="mailto:support@lifechiai.com" style={{ color: '#1a6b5a' }}>
              support@lifechiai.com
            </a>{' '}
            for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <h1 style={S.title}>Complete Your Registration</h1>

        {prefill && (
          <div style={S.prefill}>
            <strong>{prefill.tenantName}</strong> — {prefill.city}
            <br />
            <span style={{ fontSize: 12, color: '#6b7280' }}>Account: {prefill.adminEmail}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {errorMsg && <div style={S.errorBanner}>{errorMsg}</div>}

          <p style={S.sec}>Admin Account</p>

          <label style={S.lbl}>First Name *</label>
          <input name="adminFirstName" value={form.adminFirstName} onChange={handleChange}
            style={S.inp} autoComplete="given-name" />

          <label style={S.lbl}>Last Name</label>
          <input name="adminLastName" value={form.adminLastName} onChange={handleChange}
            style={S.inp} autoComplete="family-name" />

          <label style={S.lbl}>Password * (min 8 characters)</label>
          <input name="adminPassword" value={form.adminPassword} onChange={handleChange}
            style={S.inp} type="password" autoComplete="new-password" />

          <label style={S.lbl}>Phone (optional)</label>
          <input name="adminPhone" value={form.adminPhone} onChange={handleChange}
            style={S.inp} type="tel" placeholder="+919876543210" />

          <p style={S.sec}>Centre Details (optional)</p>

          <label style={S.lbl}>Address</label>
          <input name="address" value={form.address} onChange={handleChange}
            style={S.inp} autoComplete="street-address" />

          <label style={S.lbl}>Pincode</label>
          <input name="pincode" value={form.pincode} onChange={handleChange}
            onBlur={handlePincodeBlur}
            style={S.inp} placeholder="400001" />

          <label style={S.lbl}>Centre Location (optional)</label>
          <p style={S.hint}>Click the map or drag the pin to mark your centre's exact location.</p>
          <MapErrorBoundary>
          <div style={{ height: 280, borderRadius: 8, overflow: 'hidden', marginBottom: 4 }}>
            <MapContainer
              center={[20.5937, 78.9629]}
              zoom={5}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
              />
              <MapPickerInner
                pickedLat={form.pickedLat}
                pickedLng={form.pickedLng}
                flyTo={flyTo}
                onPick={handleMapPick}
              />
            </MapContainer>
          </div>
          </MapErrorBoundary>

          <label style={S.lbl}>Invoice Prefix (your centre initials, 2 to 6 letters)</label>
          <input name="invoicePrefix" value={form.invoicePrefix}
            onChange={e => setForm(f => ({ ...f, invoicePrefix: e.target.value.toUpperCase() }))}
            style={S.inp} maxLength={6} required placeholder="Your centre initials" />

          <p style={S.sec}>Payment</p>

          <label style={S.lbl}>Payment Method</label>
          <select name="paymentMethod" value={form.paymentMethod} onChange={handleChange} style={S.inp}>
            <option value="">Select (optional)</option>
            {PAYMENT_METHODS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {form.paymentMethod === 'razorpay_link' && (
            <>
              <label style={S.lbl}>Razorpay Payment Link URL *</label>
              <input name="paymentLink" value={form.paymentLink} onChange={handleChange}
                style={S.inp} placeholder="https://rzp.io/l/..." />
            </>
          )}

          <label style={S.lbl}>Healer Share % (optional)</label>
          <input name="healerSharePercent" value={form.healerSharePercent} onChange={handleChange}
            style={S.inp} type="number" min="0" max="100" placeholder="e.g. 70" />

          <p style={S.sec}>Payout</p>

          <label style={S.lbl}>Payout Frequency</label>
          <select name="payoutFrequency" value={form.payoutFrequency} onChange={handleChange} style={S.inp}>
            <option value="">Select (optional)</option>
            {PAYOUT_FREQS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {(form.payoutFrequency === 'monthly' || form.payoutFrequency === 'biweekly') && (
            <>
              <label style={S.lbl}>Payout Day of Month (1–28)</label>
              <input name="payoutDay" value={form.payoutDay} onChange={handleChange}
                style={S.inp} type="number" min="1" max="28" />
            </>
          )}

          <p style={S.sec}>GST &amp; Compliance</p>

          <label style={S.chk}>
            <input name="gstRegistered" type="checkbox" checked={form.gstRegistered} onChange={handleChange} />
            <span style={{ marginLeft: 8 }}>GST Registered</span>
          </label>

          {form.gstRegistered && (
            <>
              <label style={S.lbl}>GSTIN *</label>
              <input name="gstin" value={form.gstin} onChange={handleChange}
                style={S.inp} placeholder="22AAAAA0000A1Z5" />

              <label style={S.lbl}>Legal Entity Name</label>
              <input name="legalEntityName" value={form.legalEntityName} onChange={handleChange}
                style={S.inp} />
            </>
          )}

          <label style={S.chk}>
            <input name="mcksAffiliated" type="checkbox" checked={form.mcksAffiliated} onChange={handleChange} />
            <span style={{ marginLeft: 8 }}>MCKS Affiliated (World Pranic Healing Foundation)</span>
          </label>

          <label style={S.chk}>
            <input name="consentGiven" type="checkbox" checked={form.consentGiven} onChange={handleChange} />
            <span style={{ marginLeft: 8 }}>
              I confirm the information provided is accurate and I agree to LifeChiAI's{' '}
              <a href="https://www.lifechiai.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1a6b5a' }}>
                Privacy Policy
              </a>{' '}
              and Terms of Service.
            </span>
          </label>

          <button type="submit" style={S.btn} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}

const S = {
  page:        { minHeight: '100vh', backgroundColor: '#f0faf6', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 16px' },
  card:        { backgroundColor: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' },
  title:       { fontSize: 22, fontWeight: 800, color: '#1a3d2b', marginBottom: 12, fontFamily: "'Raleway', sans-serif" },
  prefill:     { backgroundColor: '#f0faf6', border: '1px solid #a7d7c5', color: '#1a3d2b', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 20, lineHeight: 1.6 },
  sec:         { fontSize: 13, fontWeight: 700, color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '24px 0 4px', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 },
  lbl:         { display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 14 },
  inp:         { width: '100%', padding: '12px 14px', fontSize: 16, border: '1.5px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box', outline: 'none' },
  chk:         { display: 'flex', alignItems: 'center', marginTop: 16, fontSize: 14, color: '#374151', cursor: 'pointer' },
  btn:         { marginTop: 24, width: '100%', padding: '14px 0', backgroundColor: '#1a6b5a', color: '#fff', fontSize: 16, fontWeight: 700, border: 'none', borderRadius: 10, cursor: 'pointer' },
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 12 },
  hint:        { fontSize: 12, color: '#6b7280', margin: '2px 0 8px' },
};
